<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { useProjectsStore } from '@/stores/projects'
import { ElMessageBox } from 'element-plus'
import type { UploadFile, UploadInstance } from 'element-plus'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const projectsStore = useProjectsStore()

const loading = ref(false)
const error = ref('')
const uploadRef = ref<UploadInstance>()
const checkingActive = ref(true)

const credits = computed(() => authStore.credits)

// Check for active project on mount
onMounted(async () => {
  const { hasActive, activeProject } = await projectsStore.checkHasActiveProject()
  checkingActive.value = false
  
  if (hasActive && activeProject) {
    // Show popup and redirect
    ElMessageBox.alert(
      t('project.active_warning.message', { title: activeProject.title }),
      t('project.active_warning.title'),
      {
        confirmButtonText: t('project.active_warning.view_project'),
        type: 'warning',
        center: true,
        callback: () => {
          router.push(`/project/${activeProject.id}`)
        }
      }
    )
  }
})

const form = ref({
  title: '',
  language: 'fr',
  format: 'landscape',
  visual_style: '',
  main_character_gender: '',
  is_public: true,
  mode: 'auto',
  mood_text: ''
})

const files = ref({
  audio: null as File | null,
  srt: null as File | null,
  intro: null as File | null,
  outro: null as File | null,
  character_photo: null as File | null
})

const visualStyles = [
'16mm video',
'35mm video',
'90s MTV',
'Abstract Painting',
'Absurd comedy',
'Action blockbuster',
'Animation',
'Anime / Manga',
'Anime film',
'Art Documentary',
'Artistic Black and White',
'CCTV',
'Chibi',
'Children Illustration',
'Cinematic Slow Motion',
'Cinematic film look',
'Classic cartoon (1930s/50s/90s)',
'Claymation',
'Clear line (Franco-Belgian)',
'Colorful Pop Art',
'Cyberpunk',
'Dark Cinematic Art (Low-Key, High Contrast)',
'Dark Fantasy',
'Data / Glitch Art',
'Dieselpunk',
'Dramatic shadows',
'Dreamlike Cinematic Art (Dream / Blur / Glow)',
'Editorial Fashion',
'Epic Fantasy',
'Epic space opera',
'Experimental film',
'Expressionist',
'Expressive camera',
'Face cam interview',
'Fairy Tales & Legends',
'Fantasy',
'Fashion Film',
'Film noir',
'Futurism',
'Futuristic Digital Art',
'Generative Abstract AI',
'Glossy commercial',
'Golden hour',
'Hand-Drawn Illustration',
'Hand-drawn',
'High-Contrast Black and White',
'High-concept blockbuster',
'High-key',
'Holographic',
'Hyperrealistic',
'Illustrated',
'Isometric pixel art',
'Japanese Ink (Sumi-e)',
'Kaiju',
'Melancholic / Introspective',
'Minecraft-like',
'Minimal Sci-Fi',
'Minimalist',
'Modern Cartoon',
'Modern Cinematic Art',
'Modern romance',
'Monochrome',
'Movie trailer',
'Music Video - Pop Star Style',
'Music video',
'Mystery',
'Mythology (Greek, Norse, etc.)',
'Naive Art',
'Natural light',
'Naturalism',
'Neon Synthwave',
'Neon',
'New Hollywood',
'New Wave',
'Observant camera',
'Oil Painting',
'Pencil / Charcoal',
'Period drama',
'Photorealistic',
'Pictural',
'Pixel Art',
'Pop Art',
'Pop Icon / Diva Performance',
'Post-apocalyptic',
'Realism',
'Realistic CGI / VFX film',
'Realistic Sci-Fi',
'Retro 80s',
'Retro Arcade',
'Retro Low-Poly',
'Retrofuturism',
'Road movie',
'Romantic comedy',
'Science fiction (sci-fi)',
'Shōnen / shōjo / seinen',
'Slow motion, time-lapse, freeze-frame',
'Sports broadcast',
'Spy film',
'Steampunk',
'Stop-motion',
'Street Art / Graffiti',
'Stylized Medieval',
'Stylized Stop Motion',
'Stylized',
'Super 8 video',
'Surrealist',
'Symbolic',
'Teal & orange',
'Thriller',
'Toon shading',
'Ultra-realistic',
'Urban Neon',
'VHS / Camcorder',
'Vaporwave',
'Vintage 1920s',
'Vintage Cartoon (1930s)',
'Visual Collage',
'Visual Poetics',
'War film',
'Watercolor',
'Western / neo-Western',
'Wireframe 3D',
'Y2K Aesthetic',
'YouTube creator'
]

function handleAudioChange(file: UploadFile) {
  files.value.audio = file.raw || null
  if (file.raw && !form.value.title) {
    form.value.title = file.name?.replace(/\.[^/.]+$/, '') || ''
  }
}

function handleSrtChange(file: UploadFile) {
  files.value.srt = file.raw || null
}

function handleIntroChange(file: UploadFile) {
  files.value.intro = file.raw || null
}

function handleOutroChange(file: UploadFile) {
  files.value.outro = file.raw || null
}

function handlePhotoChange(file: UploadFile) {
  files.value.character_photo = file.raw || null
}

async function createProject() {
  if (credits.value <= 0) {
    error.value = t('project.no_credits')
    return
  }
  
  if (!files.value.audio) {
    error.value = t('project.error_no_audio')
    return
  }
  
  if (!form.value.title) {
    error.value = t('project.error_no_title')
    return
  }
  
  loading.value = true
  error.value = ''
  
  try {
    const formData = new FormData()
    formData.append('title', form.value.title)
    formData.append('language', form.value.language)
    formData.append('format', form.value.format)
    formData.append('visual_style', form.value.visual_style)
    formData.append('main_character_gender', form.value.main_character_gender)
    formData.append('is_public', String(form.value.is_public))
    formData.append('mode', form.value.mode)
    formData.append('mood_text', form.value.mood_text)
    formData.append('audio_file', files.value.audio)
    
    if (files.value.srt) {
      formData.append('srt_file', files.value.srt)
    }
    if (files.value.intro) {
      formData.append('intro_file', files.value.intro)
    }
    if (files.value.outro) {
      formData.append('outro_file', files.value.outro)
    }
    if (files.value.character_photo) {
      formData.append('character_photo', files.value.character_photo)
    }
    
    const project = await projectsStore.createProject(formData)
    router.push(`/project/${project.id}`)
    
  } catch (e: any) {
    error.value = e.response?.data?.detail || 'Erreur lors de la création'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="create-view">
    <div class="create-header">
      <router-link to="/dashboard" class="back-link">
        ← {{ t('common.back') }}
      </router-link>
      <h1>{{ t('dashboard.new_project') }}</h1>
      <p>{{ t('project.create_subtitle') }}</p>
    </div>

    <el-alert 
      v-if="error" 
      type="error" 
      :title="error" 
      show-icon 
      closable
      @close="error = ''"
      class="create-alert"
    />

    <div class="create-form">
      <el-form :model="form" label-position="top">
        <!-- Audio File (Required) -->
        <div class="form-section">
          <h3>🎵 {{ t('project.audio') }} *</h3>
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :limit="1"
            accept="audio/*"
            :on-change="handleAudioChange"
            drag
            class="audio-upload"
          >
            <div class="upload-content">
              <span class="upload-icon">🎵</span>
              <p>{{ t('project.drop_audio') }}</p>
              <p class="upload-hint">{{ t('project.audio_formats') }}</p>
            </div>
          </el-upload>
        </div>

        <!-- Basic Info -->
        <div class="form-section">
          <h3>📝 {{ t('project.section_info') }}</h3>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="t('project.title')">
                <el-input v-model="form.title" placeholder="Titre de votre vidéo" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="t('project.language')">
                <el-select v-model="form.language" class="full-width">
                  <el-option value="fr" label="🇫🇷 Français" />
                  <el-option value="en" label="🇬🇧 English" />
                  <el-option value="es" label="🇪🇸 Español" />
                  <el-option value="it" label="🇮🇹 Italiano" />
                  <el-option value="de" label="🇩🇪 Deutsch" />
                  <el-option value="pt" label="🇵🇹 Português" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="t('project.format')">
                <el-radio-group v-model="form.format" class="format-group">
                  <el-radio-button value="landscape">
                    📺 {{ t('project.format.landscape') }}
                  </el-radio-button>
                  <el-radio-button value="portrait">
                    📱 {{ t('project.format.portrait') }}
                  </el-radio-button>
                  <el-radio-button value="square">
                    ⬛ {{ t('project.format.square') }}
                  </el-radio-button>
                </el-radio-group>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="t('project.style')">
                <el-select 
                  v-model="form.visual_style" 
                  filterable 
                  allow-create
                  placeholder="Choisir ou créer un style"
                  class="full-width"
                >
                  <el-option 
                    v-for="style in visualStyles" 
                    :key="style" 
                    :value="style" 
                    :label="style" 
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item :label="t('project.mood')">
            <el-input 
              v-model="form.mood_text" 
              type="textarea" 
              :rows="3"
              placeholder="Décrivez l'ambiance, le thème, l'histoire que vous souhaitez..."
            />
          </el-form-item>
        </div>

        <!-- Mode -->
        <div class="form-section">
          <h3>⚙️ {{ t('project.mode') }}</h3>
          
          <el-radio-group v-model="form.mode" class="mode-group">
            <div class="mode-option" :class="{ active: form.mode === 'auto' }">
              <el-radio value="auto">
                <div class="mode-content">
                  <span class="mode-icon">🚀</span>
                  <div>
                    <strong>{{ t('project.mode.auto') }}</strong>
                    <p>{{ t('project.mode.auto_desc') }}</p>
                  </div>
                </div>
              </el-radio>
            </div>
            <div class="mode-option" :class="{ active: form.mode === 'manual' }">
              <el-radio value="manual">
                <div class="mode-content">
                  <span class="mode-icon">🎨</span>
                  <div>
                    <strong>{{ t('project.mode.manual') }}</strong>
                    <p>{{ t('project.mode.manual_desc') }}</p>
                  </div>
                </div>
              </el-radio>
            </div>
          </el-radio-group>
        </div>

        <!-- Optional Files -->
        <div class="form-section">
          <h3>📁 {{ t('project.section_files') }}</h3>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="t('project.srt')">
                <el-upload
                  :auto-upload="false"
                  :limit="1"
                  accept=".srt,.vtt"
                  :on-change="handleSrtChange"
                >
                  <template #trigger>
                    <el-button>{{ t('project.add_subtitles') }}</el-button>
                  </template>
                </el-upload>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="t('project.photo')">
                <el-upload
                  :auto-upload="false"
                  :limit="1"
                  accept="image/*"
                  :on-change="handlePhotoChange"
                >
                  <template #trigger>
                    <el-button>{{ t('project.add_photo') }}</el-button>
                  </template>
                </el-upload>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="t('project.intro')">
                <el-upload
                  :auto-upload="false"
                  :limit="1"
                  accept="video/*"
                  :on-change="handleIntroChange"
                >
                  <template #trigger>
                    <el-button>{{ t('project.add_intro') }}</el-button>
                  </template>
                </el-upload>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="t('project.outro')">
                <el-upload
                  :auto-upload="false"
                  :limit="1"
                  accept="video/*"
                  :on-change="handleOutroChange"
                >
                  <template #trigger>
                    <el-button>{{ t('project.add_outro') }}</el-button>
                  </template>
                </el-upload>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- Options -->
        <div class="form-section">
          <el-form-item>
            <el-checkbox v-model="form.is_public">
              {{ t('project.public') }}
            </el-checkbox>
          </el-form-item>
        </div>

        <!-- Submit -->
        <div class="form-actions">
          <div class="credits-info">
            <span class="credits-icon">💎</span>
            <span>{{ t('project.available_credits') }}: <strong>{{ credits }}</strong></span>
          </div>
          
          <el-button 
            type="primary" 
            size="large"
            :loading="loading"
            :disabled="credits <= 0"
            @click="createProject"
          >
            {{ t('project.create') }}
          </el-button>
          <p v-if="credits <= 0" class="no-credits-warning">
            {{ t('project.no_credits') }}
          </p>
        </div>
      </el-form>
    </div>
  </div>
</template>

<style scoped>
.create-view {
  min-height: calc(100vh - 64px);
  padding: var(--m2v-space-8) var(--m2v-space-6);
  max-width: 900px;
  margin: 0 auto;
}

.create-header {
  margin-bottom: var(--m2v-space-8);
}

.back-link {
  display: inline-block;
  color: var(--m2v-text-muted);
  margin-bottom: var(--m2v-space-4);
  transition: color var(--m2v-transition-fast);
}

.back-link:hover {
  color: var(--m2v-primary-light);
}

.create-header h1 {
  font-family: var(--m2v-font-display);
  font-size: 2rem;
  margin-bottom: var(--m2v-space-2);
}

.create-header p {
  color: var(--m2v-text-muted);
}

.create-alert {
  margin-bottom: var(--m2v-space-6);
}

.create-form {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-8);
}

.form-section {
  margin-bottom: var(--m2v-space-8);
  padding-bottom: var(--m2v-space-6);
  border-bottom: 1px solid var(--m2v-border-subtle);
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.form-section h3 {
  font-size: 1.125rem;
  margin-bottom: var(--m2v-space-4);
}

.audio-upload {
  width: 100%;
}

.audio-upload :deep(.el-upload-dragger) {
  width: 100%;
  padding: var(--m2v-space-8);
  background: var(--m2v-bg-elevated);
  border: 2px dashed var(--m2v-border-default);
  border-radius: var(--m2v-radius-lg);
}

.audio-upload :deep(.el-upload-dragger:hover) {
  border-color: var(--m2v-primary);
}

.upload-content {
  text-align: center;
}

.upload-icon {
  font-size: 3rem;
  display: block;
  margin-bottom: var(--m2v-space-4);
}

.upload-hint {
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
}

.full-width {
  width: 100%;
}

.format-group {
  display: flex;
  flex-wrap: wrap;
  gap: var(--m2v-space-2);
}

.mode-group {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-3);
  width: 100%;
}

.mode-option {
  padding: var(--m2v-space-4);
  background: var(--m2v-bg-elevated);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  transition: all var(--m2v-transition-fast);
}

.mode-option:hover {
  border-color: var(--m2v-border-emphasis);
}

.mode-option.active {
  border-color: var(--m2v-primary);
  background: var(--m2v-gradient-card);
}

.mode-content {
  display: flex;
  align-items: flex-start;
  gap: var(--m2v-space-3);
}

.mode-icon {
  font-size: 1.5rem;
}

.mode-content p {
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
  margin-top: var(--m2v-space-1);
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--m2v-space-6);
  border-top: 1px solid var(--m2v-border-subtle);
  margin-top: var(--m2v-space-6);
}

.credits-info {
  display: flex;
  align-items: center;
  gap: var(--m2v-space-2);
  color: var(--m2v-text-muted);
}

.credits-icon {
  font-size: 1.25rem;
}

.no-credits-warning {
  color: var(--el-color-danger);
  font-size: 0.875rem;
  margin-top: var(--m2v-space-2);
}
</style>
